import { useId, useMemo, useState } from 'react'

import FbButton from 'components/FbUI/FbButton'
import { Modal } from 'components/UI/Modal'

export interface BulkEditableColumn {
  id: number | string
  label: string
  helperText?: string
}

interface Props {
  show: boolean
  onClose: () => void
  columns: BulkEditableColumn[]
  selectedRowsCount: number
  onApply: (columnId: BulkEditableColumn['id'], value: string) => Promise<void>
}

export function BulkEditSelectedRowsModal({
  show,
  onClose,
  columns,
  selectedRowsCount,
  onApply,
}: Props) {
  const columnSelectId = useId()
  const valueTextareaId = useId()
  const [selectedColumnId, setSelectedColumnId] = useState<
    BulkEditableColumn['id'] | ''
  >(columns[0]?.id ?? '')
  const [value, setValue] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selectedColumn = useMemo(
    () =>
      columns.find((column) => String(column.id) === String(selectedColumnId)),
    [columns, selectedColumnId]
  )
  const activeColumn = selectedColumn ?? columns[0]
  const activeColumnId = activeColumn?.id ?? ''

  const handleApply = async () => {
    if (!activeColumn) return
    setError(null)
    setIsSaving(true)
    try {
      await onApply(activeColumn.id, value)
      setValue('')
      onClose()
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update selected rows'
      )
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Bulk Edit Selected Rows</Modal.Title>
      </Modal.Header>
      <Modal.Body className="space-y-4 px-6 pb-5 pt-3">
        <p className="text-sm text-fg-secondary">
          Set one value for the selected rows. This overwrites existing values
          in the chosen column and marks those cells as manually edited.
        </p>

        <div>
          <label
            htmlFor={columnSelectId}
            className="block text-sm font-medium text-fg"
          >
            Column
          </label>
          <select
            id={columnSelectId}
            value={activeColumnId}
            onChange={(event) => setSelectedColumnId(event.target.value)}
            className="mt-1 w-full rounded-md border border-line-form bg-white px-3 py-2 text-sm text-fg focus:outline-none focus:ring-1 focus:ring-line-focus"
          >
            {columns.map((column) => (
              <option key={column.id} value={column.id}>
                {column.label}
              </option>
            ))}
          </select>
          {activeColumn?.helperText && (
            <p className="mt-1 text-xs text-fg-secondary">
              {activeColumn.helperText}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor={valueTextareaId}
            className="block text-sm font-medium text-fg"
          >
            Value
          </label>
          <textarea
            id={valueTextareaId}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            rows={4}
            className="mt-1 w-full resize-y rounded-md border border-line-form px-3 py-2 text-sm text-fg focus:outline-none focus:ring-1 focus:ring-line-focus"
            placeholder="Type or paste the value to apply to selected rows."
          />
          <p className="mt-1 text-xs text-fg-secondary">
            Leave blank only if you want to clear this value for the selected
            rows.
          </p>
        </div>

        <div className="rounded-md border border-line bg-surface-alt px-3 py-2 text-xs text-fg-secondary">
          {selectedRowsCount} selected row{selectedRowsCount === 1 ? '' : 's'}{' '}
          will be updated.
        </div>

        {error && (
          <div className="rounded-md border border-error-border bg-error-bg p-3 text-sm text-error">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <FbButton
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </FbButton>
          <FbButton
            type="button"
            size="sm"
            onClick={handleApply}
            disabled={!activeColumn || selectedRowsCount === 0}
            loading={isSaving}
          >
            Apply to Selected
          </FbButton>
        </div>
      </Modal.Body>
    </Modal>
  )
}
